import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  AssignedBillingInfo,
  BillingRegion,
  BillingSubscriptionItem,
  ClientBillingPaymentMethod,
  RECOVERED_AMOUNT_METER,
  UpcomingInvoice,
  UpcomingInvoiceLine,
} from "../types/billing";
import { formatCurrency } from "./formatCurrency";

dayjs.extend(utc);

export interface BillingEntity {
  name: string;
  currency: BillingRegion;
}

/**
 * Chargemont's two billing entities. The admin's real decision isn't currency —
 * it's which legal entity invoices the client. Surface entity-first everywhere
 * this choice is shown or confirmed.
 */
export const BILLING_ENTITIES: Record<BillingRegion, BillingEntity> = {
  EUR: { name: "Chargemont UAB", currency: "EUR" },
  USD: { name: "Chargemont LLC", currency: "USD" },
};

export const METER_NAME_LABELS: Record<string, string> = {
  ethoca_alerts: "Ethoca",
  cdrn_alerts: "CDRN",
  rdr_alerts: "RDR",
};

/**
 * Render an ISO timestamp as `Apr 23, 2026, 14:22 UTC`. Always UTC because
 * billing periods themselves are UTC-calendar-month — local-time rendering
 * would lie about which side of a period boundary a moment falls on.
 */
export function formatUtcDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return dayjs.utc(iso).format("MMM D, YYYY, HH:mm [UTC]");
}

export function formatPaymentMethod(pm: ClientBillingPaymentMethod): string {
  const last4 = pm.last4 ? `•••• ${pm.last4}` : null;
  const exp =
    pm.exp_month && pm.exp_year
      ? ` (exp ${String(pm.exp_month).padStart(2, "0")}/${pm.exp_year})`
      : "";

  if (pm.brand) {
    return `${pm.brand.toUpperCase()}${last4 ? " " + last4 : ""}${exp}`;
  }
  if (pm.bank) {
    return `${pm.bank}${last4 ? " " + last4 : ""} (${pm.type})`;
  }
  if (last4) {
    return `${pm.type} ${last4}`;
  }
  return pm.type;
}

export function isRecoveredAmountPrice(item: BillingSubscriptionItem): boolean {
  return item.meter_event_name === RECOVERED_AMOUNT_METER;
}

// A recovered-amount price is cents per `package_size` currency units recovered, so it reads as a
// share: 19 cents per unit is "19%". Any other price reads as its amount alone: "$12.00".
export function formatUnitPrice(item: BillingSubscriptionItem, currency: string): string | null {
  if (item.amount == null) return null;
  if (isRecoveredAmountPrice(item)) return `${Number((item.amount / (item.package_size ?? 1)).toFixed(4))}%`;
  return formatCurrency(item.amount, currency);
}

export interface ProjectedInvoiceRow {
  description: string;
  /** The line's billing period; null on a zero-usage row Stripe projects no line for. */
  period: string | null;
  unitPrice: string | null;
  /** Currency units recovered on a recovered-amount price, otherwise a unit count. */
  quantity: number | null;
  recoveredAmount: boolean;
  amount: number;
}

// One row per upcoming-invoice line grouped under its subscription product (Stripe can project
// several per price, e.g. a proration credit and charge), a zero-usage row for products Stripe
// projects no line for, followed by any lines that belong to no product (fees, discounts).
export function projectedInvoiceRows(upcomingInvoice: UpcomingInvoice, items: BillingSubscriptionItem[]): ProjectedInvoiceRow[] {
  const linesByItem = new Map<BillingSubscriptionItem, UpcomingInvoiceLine[]>();
  const unmatched: UpcomingInvoiceLine[] = [];
  for (const line of upcomingInvoice.lines) {
    const item = items.find((it) => it.price_id === line.price_id);
    if (item) linesByItem.set(item, [...(linesByItem.get(item) ?? []), line]);
    else unmatched.push(line);
  }

  return [
    ...items.flatMap((item) => {
      const description = item.product_name ?? item.nickname ?? item.price_id;
      const unitPrice = formatUnitPrice(item, upcomingInvoice.currency);
      const recoveredAmount = isRecoveredAmountPrice(item);
      const lines = linesByItem.get(item) ?? [];
      if (lines.length === 0) return [{ description, period: null, unitPrice, quantity: 0, recoveredAmount, amount: 0 }];
      return lines.map((line) => ({
        description,
        period: linePeriod(line),
        unitPrice,
        quantity: line.quantity,
        recoveredAmount,
        amount: line.amount,
      }));
    }),
    ...unmatched.map((line) => ({
      description: cleanInvoiceLineDescription(line.description) || "-",
      period: linePeriod(line),
      unitPrice: null,
      quantity: line.quantity,
      recoveredAmount: false,
      amount: line.amount,
    })),
  ];
}

function linePeriod(line: UpcomingInvoiceLine): string | null {
  return line.period_start && line.period_end ? formatDateRange(line.period_start, line.period_end) : null;
}

export function formatDateRange(start: string | null, end: string | null): string {
  if (!start || !end) return "-";
  return `${dayjs(start).format("MMM D, YYYY")} – ${dayjs(end).format("MMM D, YYYY")}`;
}

// The currency the customer is billed in, which is not the region: Chargemont UAB (EUR) clients can be priced in USD.
export function billingCurrency(info: AssignedBillingInfo): string {
  return info.subscriptions[0]?.currency
    ?? BILLING_ENTITIES[info.region].currency;
}

export type ChipColor = "success" | "warning" | "default" | "error";

export function subscriptionStatusColor(status: string): ChipColor {
  return status === "active" || status === "trialing" ? "success" : "default";
}

export function invoiceStatusColor(displayStatus: string): ChipColor {
  if (displayStatus === "Paid") return "success";
  if (displayStatus === "Pending" || displayStatus === "Open") return "warning";
  if (displayStatus === "Past due" || displayStatus === "Uncollectible") return "error";
  return "default" as const;
}

/**
 * Stripe auto-generates line item descriptions like
 * "0 × RDR Alert Processing (at €12.00 / month)". Strip the leading "at "
 * and the trailing "/ interval" so the inline price reads cleaner.
 */
export function cleanInvoiceLineDescription(description: string | null): string | null {
  if (!description) return description;
  return description
    .replace(/\(at /g, '(')
    .replace(/ \/ \w+\)/g, ')');
}
